import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  CardContent,
  CardHeader,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Checkbox,
  FormGroup,
  FormLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';

// Import components
import PageHeader from '../../components/ui/PageHeader';
import { Card, TextField, Select } from '../../design-system';
import TabPanel from '../../components/ui/TabPanel';
import { EmptyState } from '../../design-system/components/core';

// Import stores
import useNotificationStore from '../../stores/notificationStore';
import usePayrollStore from '../../stores/payrollStore';

const PayrollSettings = () => {
  const { showSuccess, showError } = useNotificationStore();
  const { fetchPayrollSettings, updatePayrollSettings, isLoading } = usePayrollStore();
  
  const [tabValue, setTabValue] = useState(0);
  const [settings, setSettings] = useState({
    // General Settings
    companyName: 'Aghrba Marketing System',
    payFrequency: 'monthly',
    defaultCurrency: 'EGP',
    taxRate: 25,
    overtimeRate: 1.5,
    autoCalculateOvertime: true,
    autoGeneratePayslips: true,
    workingDaysPerMonth: 22,
    workingHoursPerDay: 8,
    
    // Social Insurance
    socialInsurance: {
      employeeRate: 0.11,
      employerRate: 0.19
    },
    
    // Payment Methods
    paymentMethods: ['bank_transfer', 'cash'],
    
    // Tax Configuration
    taxRates: {
      brackets: []
    },
    
    // Pay Components
    basicSalaryEnabled: true,
    allowancesEnabled: true,
    overtimeEnabled: true,
    bonusEnabled: true,
    
    // Deductions
    taxEnabled: true,
    socialSecurityEnabled: true,
    healthInsuranceEnabled: true,
    pensionEnabled: true
  });

  const [payComponents, setPayComponents] = useState([
    { id: 1, name: 'Basic Salary', type: 'earning', mandatory: true, taxable: true },
    { id: 2, name: 'Housing Allowance', type: 'earning', mandatory: false, taxable: true },
    { id: 3, name: 'Transport Allowance', type: 'earning', mandatory: false, taxable: false },
    { id: 4, name: 'Overtime', type: 'earning', mandatory: false, taxable: true },
    { id: 5, name: 'Income Tax', type: 'deduction', mandatory: true, taxable: false },
    { id: 6, name: 'Social Security', type: 'deduction', mandatory: true, taxable: false },
    { id: 7, name: 'Health Insurance', type: 'deduction', mandatory: false, taxable: false }
  ]);

  const [deductionRules, setDeductionRules] = useState([
    { id: 1, name: 'Income Tax', type: 'percentage', value: 25, minAmount: 0, maxAmount: null },
    { id: 2, name: 'Social Security', type: 'percentage', value: 8, minAmount: 0, maxAmount: 1000 },
    { id: 3, name: 'Health Insurance', type: 'fixed', value: 150, minAmount: 150, maxAmount: 150 }
  ]);

  const [componentDialogOpen, setComponentDialogOpen] = useState(false);
  const [newComponent, setNewComponent] = useState({
    name: '',
    type: 'earning',
    mandatory: false,
    taxable: true
  });
  
  // Fetch settings on mount
  useEffect(() => {
    fetchSettings();
  }, []);
  
  const fetchSettings = async () => {
    try {
      const fetchedSettings = await fetchPayrollSettings();
      if (fetchedSettings) {
        // Merge fetched settings with local state
        setSettings(prev => ({
          ...prev,
          ...fetchedSettings
        }));
      }
    } catch (error) {
      showError('Failed to load payroll settings');
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSettingsChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSettings = async () => {
    try {
      await updatePayrollSettings(settings);
      showSuccess('Payroll settings saved successfully');
    } catch (error) {
      showError('Failed to save payroll settings');
    }
  };

  const handleAddComponent = () => {
    const component = {
      id: payComponents.length + 1,
      ...newComponent
    };
    setPayComponents(prev => [...prev, component]);
    setNewComponent({ name: '', type: 'earning', mandatory: false, taxable: true });
    setComponentDialogOpen(false);
    showSuccess('Pay component added successfully');
  };

  const handleDeleteComponent = (id) => {
    setPayComponents(prev => prev.filter(comp => comp.id !== id));
    showSuccess('Pay component deleted successfully');
  };
  
  // Tax bracket handlers
  const handleAddTaxBracket = () => {
    const currentBrackets = settings.taxRates?.brackets || [];
    const lastBracket = currentBrackets[currentBrackets.length - 1];
    const newMin = lastBracket ? (lastBracket.max || 0) : 0;
    
    const newBracket = {
      min: newMin,
      max: null,
      rate: 0
    };
    
    handleSettingsChange('taxRates', {
      brackets: [...currentBrackets, newBracket]
    });
  };
  
  const handleUpdateTaxBracket = (index, field, value) => {
    const currentBrackets = settings.taxRates?.brackets || [];
    const updatedBrackets = [...currentBrackets];
    updatedBrackets[index] = {
      ...updatedBrackets[index],
      [field]: value
    };
    
    handleSettingsChange('taxRates', {
      brackets: updatedBrackets
    });
  };
  
  const handleDeleteTaxBracket = (index) => {
    const currentBrackets = settings.taxRates?.brackets || [];
    const updatedBrackets = currentBrackets.filter((_, i) => i !== index);
    
    handleSettingsChange('taxRates', {
      brackets: updatedBrackets
    });
  };
  
  const handleLoadEgyptianTaxBrackets = () => {
    const egyptianBrackets = [
      { min: 0, max: 60000, rate: 0 },
      { min: 60000, max: 70000, rate: 2.5 },
      { min: 70000, max: 80000, rate: 10 },
      { min: 80000, max: 90000, rate: 15 },
      { min: 90000, max: 200000, rate: 20 },
      { min: 200000, max: 400000, rate: 22.5 },
      { min: 400000, max: 600000, rate: 25 },
      { min: 600000, max: 700000, rate: 27.5 },
      { min: 700000, max: null, rate: 27.5 }
    ];
    
    handleSettingsChange('taxRates', {
      brackets: egyptianBrackets
    });
    
    showSuccess('Egyptian tax brackets loaded successfully');
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Payroll Settings"
        subtitle="Configure payroll calculations and components"
      />

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="General Settings" />
            <Tab label="Pay Components" />
            <Tab label="Deduction Rules" />
            <Tab label="Tax Configuration" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Company Information
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  label="Company Name"
                  value={settings.companyName}
                  onChange={(e) => handleSettingsChange('companyName', e.target.value)}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Select
                  label="Default Currency"
                  value={settings.defaultCurrency}
                  onChange={(value) => handleSettingsChange('defaultCurrency', value)}
                  options={[
                    { value: 'EGP', label: 'Egyptian Pound (EGP)' },
                    { value: 'USD', label: 'US Dollar (USD)' },
                    { value: 'EUR', label: 'Euro (EUR)' },
                    { value: 'GBP', label: 'British Pound (GBP)' },
                    { value: 'AED', label: 'UAE Dirham (AED)' },
                    { value: 'SAR', label: 'Saudi Riyal (SAR)' },
                    { value: 'KWD', label: 'Kuwaiti Dinar (KWD)' },
                    { value: 'QAR', label: 'Qatari Riyal (QAR)' }
                  ]}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Payroll Settings
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Select
                  label="Pay Frequency"
                  value={settings.payFrequency}
                  onChange={(value) => handleSettingsChange('payFrequency', value)}
                  options={[
                    { value: 'weekly', label: 'Weekly' },
                    { value: 'bi-weekly', label: 'Bi-Weekly' },
                    { value: 'monthly', label: 'Monthly' },
                    { value: 'quarterly', label: 'Quarterly' }
                  ]}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Default Tax Rate (%)"
                  type="number"
                  value={settings.taxRate}
                  onChange={(e) => handleSettingsChange('taxRate', parseFloat(e.target.value))}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Overtime Rate Multiplier"
                  type="number"
                  step="0.1"
                  value={settings.overtimeRate}
                  onChange={(e) => handleSettingsChange('overtimeRate', parseFloat(e.target.value))}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Working Days Per Month"
                  type="number"
                  value={settings.workingDaysPerMonth || 22}
                  onChange={(e) => handleSettingsChange('workingDaysPerMonth', parseInt(e.target.value))}
                  fullWidth
                  helperText="Used for salary calculations"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Working Hours Per Day"
                  type="number"
                  value={settings.workingHoursPerDay || 8}
                  onChange={(e) => handleSettingsChange('workingHoursPerDay', parseInt(e.target.value))}
                  fullWidth
                  helperText="Standard working hours"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Social Insurance Rates
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Employee Contribution Rate (%)"
                  type="number"
                  step="0.01"
                  value={(settings.socialInsurance?.employeeRate || 0.11) * 100}
                  onChange={(e) => handleSettingsChange('socialInsurance', { 
                    ...settings.socialInsurance, 
                    employeeRate: parseFloat(e.target.value) / 100 
                  })}
                  fullWidth
                  helperText="Deducted from employee salary"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Employer Contribution Rate (%)"
                  type="number"
                  step="0.01"
                  value={(settings.socialInsurance?.employerRate || 0.19) * 100}
                  onChange={(e) => handleSettingsChange('socialInsurance', { 
                    ...settings.socialInsurance, 
                    employerRate: parseFloat(e.target.value) / 100 
                  })}
                  fullWidth
                  helperText="Paid by employer"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Automation Settings
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.autoCalculateOvertime}
                      onChange={(e) => handleSettingsChange('autoCalculateOvertime', e.target.checked)}
                    />
                  }
                  label="Auto-calculate overtime"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.autoGeneratePayslips}
                      onChange={(e) => handleSettingsChange('autoGeneratePayslips', e.target.checked)}
                    />
                  }
                  label="Auto-generate payslips"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Payment Methods
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Select the payment methods available for salary disbursement
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormGroup row>
                  {[
                    { value: 'bank_transfer', label: 'Bank Transfer' },
                    { value: 'cash', label: 'Cash' },
                    { value: 'cheque', label: 'Cheque' },
                    { value: 'instapay', label: 'InstaPay' },
                    { value: 'paypal', label: 'PayPal' },
                    { value: 'cryptocurrency', label: 'Cryptocurrency' },
                    { value: 'other', label: 'Other' }
                  ].map((method) => (
                    <FormControlLabel
                      key={method.value}
                      control={
                        <Checkbox
                          checked={(settings.paymentMethods || []).includes(method.value)}
                          onChange={(e) => {
                            const currentMethods = settings.paymentMethods || [];
                            if (e.target.checked) {
                              handleSettingsChange('paymentMethods', [...currentMethods, method.value]);
                            } else {
                              handleSettingsChange('paymentMethods', currentMethods.filter(m => m !== method.value));
                            }
                          }}
                        />
                      }
                      label={method.label}
                      sx={{ width: '200px' }}
                    />
                  ))}
                </FormGroup>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveSettings}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Saving...' : 'Save Settings'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Pay Components
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setComponentDialogOpen(true)}
              >
                Add Component
              </Button>
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Mandatory</TableCell>
                    <TableCell>Taxable</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payComponents.map((component) => (
                    <TableRow key={component.id}>
                      <TableCell>{component.name}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={component.type === 'earning' ? 'success.main' : 'error.main'}
                        >
                          {component.type === 'earning' ? 'Earning' : 'Deduction'}
                        </Typography>
                      </TableCell>
                      <TableCell>{component.mandatory ? 'Yes' : 'No'}</TableCell>
                      <TableCell>{component.taxable ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        <IconButton size="small">
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDeleteComponent(component.id)}
                          disabled={component.mandatory}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Deduction Rules
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Configure automatic deduction calculations based on salary amounts.
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Deduction</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell>Min Amount</TableCell>
                    <TableCell>Max Amount</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deductionRules.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell>{rule.name}</TableCell>
                      <TableCell>{rule.type === 'percentage' ? 'Percentage' : 'Fixed Amount'}</TableCell>
                      <TableCell>
                        {rule.type === 'percentage' ? `${rule.value}%` : `$${rule.value}`}
                      </TableCell>
                      <TableCell>${rule.minAmount}</TableCell>
                      <TableCell>{rule.maxAmount ? `$${rule.maxAmount}` : 'No limit'}</TableCell>
                      <TableCell>
                        <IconButton size="small">
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <CardContent>
            <Alert severity="info" sx={{ mb: 3 }}>
              Tax configuration has been moved to a centralized location for better management.
            </Alert>
            
            <Typography variant="h6" gutterBottom>
              Tax Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              All tax settings including income tax brackets, VAT, social insurance, and withholding taxes are now managed in a single unified location.
            </Typography>
            
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<SettingsIcon />}
                onClick={() => window.location.href = '/settings/tax-configuration'}
              >
                Open Tax Configuration
              </Button>
            </Box>
          </CardContent>
        </TabPanel>
      </Card>

      {/* Add Component Dialog */}
      <Dialog open={componentDialogOpen} onClose={() => setComponentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Pay Component</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Component Name"
                value={newComponent.name}
                onChange={(e) => setNewComponent(prev => ({ ...prev, name: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Select
                label="Type"
                value={newComponent.type}
                onChange={(value) => setNewComponent(prev => ({ ...prev, type: value }))}
                options={[
                  { value: 'earning', label: 'Earning' },
                  { value: 'deduction', label: 'Deduction' }
                ]}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newComponent.mandatory}
                    onChange={(e) => setNewComponent(prev => ({ ...prev, mandatory: e.target.checked }))}
                  />
                }
                label="Mandatory Component"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newComponent.taxable}
                    onChange={(e) => setNewComponent(prev => ({ ...prev, taxable: e.target.checked }))}
                  />
                }
                label="Taxable Component"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComponentDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddComponent} variant="contained">Add Component</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayrollSettings;